// Shared HTTP client for the StartUs API — the one place that turns fetch results into data or a
// typed ApiError. Replaces the apiRequest/handleResponse/throwApiError copies across services.
// Auth (Bearer + refresh on 401) stays in fetchWithAuth; this layer adds JSON headers, error
// parsing, empty-body handling and network-error mapping.

import { fetchWithAuth } from './fetchWithAuth';
import type { ApiError } from '../errors/types';

const API_BASE_URL = import.meta.env.VITE_API_URL;

if (!API_BASE_URL) {
  throw new Error('VITE_API_URL is not defined');
}

export interface ApiRequestOptions extends RequestInit {
  // false → plain fetch for public endpoints (no token, no refresh-on-401 logout).
  auth?: boolean;
}

export interface PaginationMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasPrevious: boolean;
  hasNext: boolean;
}

export interface PaginatedResult<T> {
  data: T[];
  meta: PaginationMeta;
}

// Same message admin/hub already surface; status 0 marks "never reached the API".
const NETWORK_ERROR: ApiError = {
  status: 0,
  code: undefined,
  message: 'Network error: Failed to connect to the API',
};

export function isApiError(error: unknown): error is ApiError {
  return typeof error === 'object' && error !== null && 'status' in error;
}

/** Builds the ApiError for a non-2xx response (JSON body, text body or none). */
export async function toApiError(response: Response): Promise<ApiError> {
  const text = await response.text().catch(() => '');
  let body: Record<string, unknown> | null = null;
  try {
    const parsed: unknown = text ? JSON.parse(text) : null;
    body =
      typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, unknown>) : null;
  } catch {
    body = null;
  }

  const pick = (key: string) =>
    typeof body?.[key] === 'string' ? (body[key] as string) : undefined;
  return {
    status: response.status,
    code: pick('code'),
    message: pick('message') ?? pick('error') ?? response.statusText,
  };
}

// String bodies are our JSON payloads. FormData/Blob need the browser-set Content-Type
// (multipart boundary), and body-less requests (GET/DELETE) skip it so public GETs stay
// "simple" CORS requests without a preflight.
function buildHeaders(options: RequestInit): HeadersInit {
  return typeof options.body === 'string'
    ? { 'Content-Type': 'application/json', ...options.headers }
    : { ...options.headers };
}

/**
 * Calls `${VITE_API_URL}${endpoint}` and returns the parsed JSON body as-is.
 * Throws ApiError on non-2xx (with the BE `code`) and on network failure (status 0).
 * 204/empty responses resolve to `{}` (matches the previous admin/hub client).
 */
export async function apiRequest<T>(endpoint: string, options: ApiRequestOptions = {}): Promise<T> {
  const { auth = true, ...init } = options;
  const request = { ...init, headers: buildHeaders(init) };
  const url = `${API_BASE_URL}${endpoint}`;

  let response: Response;
  try {
    response = auth ? await fetchWithAuth(url, request) : await fetch(url, request);
  } catch (error) {
    // fetchWithAuth throws an ApiError itself when the session can't be refreshed.
    if (isApiError(error)) throw error;
    throw { ...NETWORK_ERROR } satisfies ApiError;
  }

  if (!response.ok) throw await toApiError(response);

  const text = await response.text();
  return (text ? JSON.parse(text) : {}) as T;
}

/** Like apiRequest, but unwraps the BE `{ success, data }` envelope (bodies without one pass through). */
export async function apiRequestData<T>(endpoint: string, options?: ApiRequestOptions): Promise<T> {
  const body = await apiRequest<{ data?: T } | T>(endpoint, options);
  if (typeof body === 'object' && body !== null && 'data' in body) {
    return (body as { data: T }).data;
  }
  return body as T;
}

/** For list endpoints that return `{ data: T[], meta }`. */
export async function apiRequestPaginated<T>(
  endpoint: string,
  options?: ApiRequestOptions
): Promise<PaginatedResult<T>> {
  const body = await apiRequest<Partial<PaginatedResult<T>>>(endpoint, options);
  return { data: body.data ?? [], meta: body.meta as PaginationMeta };
}
